import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { LogoLockup } from "./Logo";

const NAV = [
  { label: "Services", id: "services" },
  { label: "Approach", id: "approach" },
  { label: "About", id: "about" },
  { label: "Team", id: "team" },
  { label: "Contact", id: "contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    if (pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 z-50 w-full transition-colors duration-500 ${
        scrolled ? "backdrop-blur-xl bg-quantum-bg/70 border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="flex items-center justify-between px-6 md:px-12 lg:px-24 py-5">
        <button
          onClick={() => scrollTo("hero")}
          data-testid="brand-logo"
          className="group"
        >
          <LogoLockup height={40} />
        </button>

        <nav className="hidden md:flex items-center gap-10">
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => scrollTo(n.id)}
              data-testid={`nav-${n.id}`}
              className="text-sm text-quantum-muted hover:text-quantum-gold transition-colors duration-300"
            >
              {n.label}
            </button>
          ))}
          <button
            onClick={() => scrollTo("contact")}
            data-testid="header-cta"
            className="text-sm px-6 py-2.5 bg-quantum-gold text-black hover:bg-white transition-colors duration-300 active:scale-95"
          >
            Get in Touch
          </button>
        </nav>

        <button
          className="md:hidden text-quantum-text"
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-quantum-bg/95 backdrop-blur-xl border-t border-white/10"
          >
            <div className="flex flex-col px-6 py-6 gap-5">
              {NAV.map((n) => (
                <button
                  key={n.id}
                  onClick={() => scrollTo(n.id)}
                  data-testid={`mobile-nav-${n.id}`}
                  className="text-left text-lg text-quantum-muted hover:text-quantum-gold transition-colors"
                >
                  {n.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
